import { useState, useCallback, useRef, useEffect } from 'react';
import { lookupPatientByABHA } from '../services/api/abhaApi';
import { getPatient } from '../services/api/visitApi';

export const ABHA_ALREADY_LINKED_CODE = 'ABHA_ALREADY_LINKED';
export const ABHA_ALREADY_LINKED_NOTE =
  'ABHA could not be linked because it is already linked to another patient.';

/**
 * One ABHA <-> one patient (owner R22). Call `check(abhaNumber)` BEFORE creating a patient: if the
 * ABHA already belongs to a patient in this org it returns true and `duplicate` holds what
 * AbhaAlreadyLinkedNotice needs; the caller must then NOT create a second record.
 * A failed lookup returns false: the backend's atomic link claim still refuses a duplicate.
 */
export default function useAbhaDuplicateGuard() {
  const [duplicate, setDuplicate] = useState(null);
  const [opening, setOpening] = useState(false);
  const openSeqRef = useRef(0);
  const openingRef = useRef(false);

  // Screen gone: a pending Open-patient fetch must never call onSelect afterwards.
  useEffect(() => () => { openSeqRef.current += 1; }, []);

  const show = useCallback(async (patientId, fallbackName) => {
    let patient = null;
    try { patient = await getPatient(patientId); } catch (_) { /* notice still shows name */ }
    openSeqRef.current += 1; // a newer notice supersedes any Open-patient fetch in flight
    openingRef.current = false;
    setOpening(false);
    setDuplicate({
      patientId,
      patientName: patient
        ? `${patient.first_name || ''} ${patient.last_name || ''}`.trim() || fallbackName
        : fallbackName,
      uhid: patient?.uhid || '',
      patient,
    });
  }, []);

  const check = useCallback(async (abhaNumber) => {
    try {
      const found = await lookupPatientByABHA({ abhaNumber });
      if (!found?.found || !found.patientId) { setDuplicate(null); return false; }
      await show(found.patientId, found.patientName);
      return true;
    } catch (_) {
      return false;
    }
  }, [show]);

  // R88: the create call itself refused (409 ABHA_ALREADY_LINKED + patient_id). Show the same notice.
  // Returns false when the error is something else, so the caller keeps its normal error banner.
  const fromError = useCallback(async (err) => {
    if (err?.code !== ABHA_ALREADY_LINKED_CODE || !err.patient_id) return false;
    await show(err.patient_id, '');
    return true;
  }, [show]);

  // Leaving the notice (back, reset, other patient, mobile change) also cancels a pending open.
  const clear = useCallback(() => {
    openSeqRef.current += 1;
    openingRef.current = false;
    setOpening(false);
    setDuplicate(null);
  }, []);

  // "Open patient": fetch the full record once per click (single-flight), then `onSelect(patient)`.
  // If the fetch also fails, selects `{ patient_id }` so the screen shows its "details could not be
  // loaded" fallback (D8/R99). A stale answer (user left the notice meanwhile) is dropped (D9/R98).
  const open = useCallback(async (onSelect) => {
    const dup = duplicate;
    if (!dup || openingRef.current) return;
    let patient = dup.patient;
    if (!patient) {
      const seq = ++openSeqRef.current;
      openingRef.current = true;
      setOpening(true);
      try { patient = await getPatient(dup.patientId); } catch (_) { patient = null; }
      if (seq !== openSeqRef.current) return;
      if (!patient?.patient_id) patient = { patient_id: dup.patientId };
    }
    clear();
    onSelect(patient);
  }, [duplicate, clear]);

  return { duplicate, opening, check, fromError, clear, open };
}
